/**
 * Face ID ADENTRO DE LA APP (iPhone).
 *
 * En el navegador el candado lo hace WebAuthn: se crea una credencial atada al
 * dominio y después se pide con `navigator.credentials.get()`. En el WebView de
 * la app eso no sirve, y por dos razones distintas: no hay autenticador de
 * plataforma, y `rp.id` sería `localhost` (el contenedor sirve desde
 * `capacitor://localhost`), o sea una credencial atada a un dominio que no es
 * el nuestro. Por eso acá va el plugin nativo.
 *
 * **La diferencia conceptual importa y está asumida.** WebAuthn guarda una
 * credencial criptográfica; Face ID nativo sólo responde "sí" o "no". Para lo
 * que FORJA usa el candado —una puerta local para que el que te agarra el
 * teléfono desbloqueado no vea tu liga— alcanza: no hay nada del lado del
 * servidor colgado de esto, la sesión ya existe, y la puerta SIEMPRE tiene la
 * salida "usar contraseña". Si alguna vez el candado pasara a autorizar algo
 * contra el backend, esto no alcanzaría y habría que volver a una credencial.
 *
 * AL PLUGIN SE LE HABLA POR EL PUENTE, SIN SU PAQUETE JS: el `import()`
 * dinámico de un plugin se colgó para siempre el 3-11 en un iPhone con iOS
 * 26.1 (la historia está en `loginNativo.ts`), y acá el síntoma sería que Face
 * ID nunca llega a preguntar. El paquete queda de plan B.
 */

import { llamarNativo } from "./nativo";

const LLAVE_NATIVA = "forja.biometria_nativa";

/**
 * El plugin nativo, por su nombre en el puente. OJO: NO es "BiometricAuth".
 * El paquete registra el nativo como `BiometricAuthNative` y le pone encima
 * una capa JS; por el puente se habla con el de abajo, con sus métodos.
 */
const PLUGIN = "BiometricAuthNative";

async function plugin() {
  const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
  return BiometricAuth;
}

/** ¿El teléfono tiene Face ID (o huella) configurado y usable? */
export async function hayBiometriaNativa(): Promise<boolean> {
  try {
    const res = await (llamarNativo<{ isAvailable?: boolean }>(PLUGIN, "checkBiometry") ??
      plugin().then((BiometricAuth) => BiometricAuth.checkBiometry()));
    return res.isAvailable === true;
  } catch {
    return false;
  }
}

/**
 * "Encender el candado" en nativo es sólo verificar una vez y dejarlo anotado:
 * no hay credencial que crear. Devuelve true si la persona se verificó.
 */
export async function encenderCandadoNativo(): Promise<boolean> {
  const ok = await pedirVerificacion("Para activar el candado de FORJA");
  if (ok === "ok") {
    try {
      localStorage.setItem(LLAVE_NATIVA, "1");
    } catch {
      return false;
    }
    return true;
  }
  return false;
}

export function candadoNativoEncendido(): boolean {
  try {
    return localStorage.getItem(LLAVE_NATIVA) === "1";
  } catch {
    return false;
  }
}

export function apagarCandadoNativo(): void {
  try {
    localStorage.removeItem(LLAVE_NATIVA);
  } catch {
    /* nada que hacer */
  }
}

export type ResultadoNativo = "ok" | "cancelado" | "no_disponible";

/**
 * Pide Face ID. Cancelar y fallar NO son lo mismo que "este teléfono no
 * puede": lo primero deja la puerta abierta para reintentar o usar la
 * contraseña, lo segundo tiene que apagar la oferta. Por eso se separan por
 * código y no por el texto del error.
 */
export async function pedirVerificacion(motivo: string): Promise<ResultadoNativo> {
  const opciones = {
    reason: motivo,
    cancelTitle: "Usar contraseña",
    iosFallbackTitle: "Usar el código del teléfono",
    allowDeviceCredential: true,
  };
  try {
    // Por el puente se llama al método nativo de verdad, `internalAuthenticate`.
    // `authenticate` es la capa JS del paquete: lo llama y re-envuelve el
    // error en uno propio con el MISMO `code`. El `code` que se mira abajo
    // llega igual por los dos caminos, porque el puente copia las propiedades
    // del error nativo tal cual.
    await (llamarNativo<void>(PLUGIN, "internalAuthenticate", opciones) ??
      plugin().then((BiometricAuth) => BiometricAuth.authenticate(opciones)));
    return "ok";
  } catch (e) {
    const code = (e as { code?: string })?.code ?? "";
    if (
      code === "userCancel" ||
      code === "appCancel" ||
      code === "systemCancel" ||
      code === "userFallback" ||
      code === "authenticationFailed"
    ) {
      return "cancelado";
    }
    return "no_disponible";
  }
}
